/* eslint-disable @typescript-eslint/no-explicit-any */
import { BeekLogo, Image, Typography } from 'beek-design-system';
import { Button } from '@beek/design-system';
import { Offer } from '../Layout/LandingPageHelpers';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import Avo from 'src/tracking/Avo';
import Const from 'src/config/Const';
import Link from 'next/link';
import PlansSection from 'src/components/LandingPage/PlansSection';
import styled from 'styled-components';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: GraphQL imports are bugged but functional. Ignored for now to make CircleCI happy.
import { Affiliate } from 'src/api/landing/landing.graphql';
import pathWithQueryString from 'src/common/utils/pathWithQueryString';

type HomeSectionProps = {
  hasDiscount?: boolean;
  price?: string;
  discountPrice?: string;
  discountPercentage?: string;
  urlImage?: string;
  interval?: number;
  intervalUnit?: string;
  affiliate?: Affiliate | null;
  offer?: Offer | null;
  dataPlans?: any;
};

type LandingPlansProps = {
  isLandingPlans?: boolean;
};

const defaultProps = {
  hasDiscount: false,
  price: '',
  discountPrice: '',
  discountPercentage: '',
  urlImage: '',
  interval: 0,
  intervalUnit: '',
  affiliate: {},
  offer: {},
  dataPlans: [],
};

const SectionContainer = styled.section<LandingPlansProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: ${(props): string => (props.isLandingPlans ? 'left' : 'center')};
  top: 0;
  @media ${({ theme }): string => theme.devices.laptop} {
    flex-direction: row;
    justify-content: space-between;
    width: 57.25rem;
  }
`;

const InfoContainer = styled.div`
  width: 100%;
  padding: 0rem 1rem 2.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  @media ${({ theme }): string => theme.devices.laptop} {
    width: 26.625rem;
    padding: 0;
    align-items: start;
    text-align: left;
  }
`;

const ButtonWrapper = styled.div`
  width: fit-content;
  font-weight: 700;
`;

const TextLine = styled(Typography)`
  text-decoration: line-through;
  color: var(--red);
`;

const LogosContainer = styled.div`
  display: flex;
  margin-bottom: 1.75rem;
`;

const VerticalDivider = styled.div`
  width: 0.063rem;
  margin: 0 1.5rem;
  height: 2rem;
  background-color: var(--gray-15);
`;

const DiscountContainer = styled.div`
  margin-bottom: 2rem;
`;

const TitleText = styled(Typography)`
  width: 100%;
  @media ${({ theme }): string => theme.devices.laptop} {
    width: 95%;
  }
`;

const HomeSection = (props: HomeSectionProps): JSX.Element => {
  const {
    hasDiscount,
    price,
    discountPrice,
    urlImage,
    offer,
    affiliate,
    interval,
    intervalUnit,
    dataPlans,
  } = props;
  const t = useTranslations('landing');
  const isPlan = dataPlans?.length !== 0;

  let months = '';
  if (intervalUnit)
    months =
      interval && interval > 1
        ? String(t(`units.${intervalUnit}s`))
        : String(t(`units.${intervalUnit}`));
  let month = '';
  if (intervalUnit) month = String(t(`units.${intervalUnit}`));
  let affiliateName = '';
  if (affiliate) affiliateName = affiliate.name;

  const handleTrackingEvent = (): void => {
    Avo.startButtonPressed({
      signupOfferSlug: offer?.slug,
      userAgent: navigator.userAgent,
    });
  };

  const signupLink = useMemo(() => {
    return pathWithQueryString('/signup', {
      offer: offer?.slug,
    });
  }, [offer?.slug]);

  return (
    <SectionContainer isLandingPlans={isPlan}>
      <InfoContainer data-testid="homeSection">
        {hasDiscount && (
          <LogosContainer data-testid="homeSection-discount-logo">
            <BeekLogo variant="small" />
            <VerticalDivider />
            {/* eslint-disable @next/next/no-img-element */}
            {urlImage ? (
              <img
                src={urlImage}
                alt="discount-logo"
                style={{ marginTop: '0.3rem' }}
              />
            ) : (
              <Image
                url=""
                alt="discount-logo"
                type="discount"
                marginTop="0.3rem"
              />
            )}
            {/* eslint-enable */}
          </LogosContainer>
        )}
        <TitleText type="headline1" colorVariant="gray100" marginBottom="1rem">
          {`${t(`home.title`)}`}
        </TitleText>
        {hasDiscount ? (
          <DiscountContainer data-testid="homeSection-discount-container">
            <Typography type="body1" colorVariant="gray100" display="inline">
              {`${t('home.discountDescription')}`}
            </Typography>
            <TextLine type="body1" colorVariant="red" display="inline">
              {price || ''}
            </TextLine>
            <Typography type="body1" colorVariant="black" display="inline">
              {` ${discountPrice}` || ''}
            </Typography>
            {affiliate?.affiliateType === 'bank' ? (
              <Typography type="body1" colorVariant="gray100" display="inline">
                {`${t('home.copyAffiliate', {
                  interval,
                  months,
                  affiliateName,
                })}`}
              </Typography>
            ) : (
              <Typography type="body1" colorVariant="black" display="inline">
                {`${t('home.intervalDiscount', { months, interval, month })}`}
              </Typography>
            )}
          </DiscountContainer>
        ) : (
          <Typography
            type="body1"
            colorVariant="gray100"
            marginBottom="2rem"
            data-testid="homeSection-description"
          >
            {`${t(isPlan ? 'home.plansDescription' : 'home.description')}`}
          </Typography>
        )}
        {isPlan ? (
          <PlansSection dataPlans={dataPlans || []} width="88%" />
        ) : (
          <ButtonWrapper
            data-cy="landing-home-ctaButton"
            data-testid="landing-home-ctaButton"
          >
            <Link href={signupLink} passHref>
              <Button onClick={handleTrackingEvent}>
                {t('home.ctaButton')}
              </Button>
            </Link>
          </ButtonWrapper>
        )}
      </InfoContainer>
      <Image
        url={`${Const.cdnUrl}/landing_page/mobile/de-cero-a-uno-peter-thiel-blake-masters-hooked-nir-eyal-ryan-hoover.png`}
        laptopUrl={`${Const.cdnUrl}/landing_page/desktop/de-cero-a-uno-peter-thiel-blake-masters-hooked-nir-eyal-ryan-hoover.png`}
        type="mosaic"
        alt={`${t('home.alt')}`}
      />
    </SectionContainer>
  );
};

HomeSection.defaultProps = defaultProps;

export default HomeSection;
